import type { Firestore } from 'firebase-admin/firestore'
import { isString, isNonEmptyString } from '../../common/utils/type-guards.js'
import type { MapPresetId, PlannerDashboardContext, PlannerLayoutPreset } from '@my-roadtrip/types'
import {
  Plan,
  Stage,
  CreatePlanDTO,
  UpdatePlanDTO,
  CreateStageDTO,
  UpdateStageDTO,
  RouteSettings,
  RouteData,
  AIGenerationMetadata,
  PlannerPreferences,
  MAP_PRESET_IDS
} from '@my-roadtrip/types'
import { createLogger } from '../../common/utils/logger.js'
import { SmartBatch, SOFT_DELETE_FIELD, createBatchBuilder } from '../../common/utils/firestore-batch.js'
import type { FirestoreDoc } from '../../common/types/firestore.js'
import { normalizeLocation, toDateOrUndefined, toTimestamp } from '../../common/utils/normalize.js'

const logger = createLogger('PlannerRepository')

const PLANNER_LAYOUT_PRESETS: readonly PlannerLayoutPreset[] = [
  'balanced',
  'focus-form',
  'focus-map',
  'compact',
  'custom',
  'editor-pro',
  'stages-hub',
  'map-immersive',
  'insights-lab'
] as const

const DASHBOARD_CONTEXTS: readonly PlannerDashboardContext[] = ['pre', 'during', 'spark'] as const

function isValidMapPreset(value: unknown): value is MapPresetId {
  return isString(value)&& (MAP_PRESET_IDS as readonly string[]).includes(value as MapPresetId)
}

function isValidLayoutPreset(value: unknown): value is PlannerLayoutPreset {
  return isString(value)&& (PLANNER_LAYOUT_PRESETS as readonly string[]).includes(value as PlannerLayoutPreset)
}

function isValidDashboardContext(value: unknown): value is PlannerDashboardContext {
  return isString(value)&& (DASHBOARD_CONTEXTS as readonly string[]).includes(
    value as PlannerDashboardContext
  )
}

export class PlannerRepository {
  private readonly plansCollection = 'plans'
  private readonly stagesCollection = 'stages'
  private readonly usersCollection = 'users'

  constructor(private readonly firestore: Firestore) {}

  private sanitizeDocument<T>(data: T): T {
    if (data === null || data === undefined) {
      return data
    }

    if (Array.isArray(data)) {
      return data
        .map(item => (typeof item === 'object' && item !== null ? this.sanitizeDocument(item) : item))
        .filter(item => item !== undefined) as unknown as T
    }

    if (data instanceof Date) {
      return data
    }

    if (typeof data === 'object') {
      const result: Record<string, unknown> = {}
      for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
        if (value === undefined) {
          continue
        }

        if (Array.isArray(value) || (typeof value === 'object' && value !== null && !(value instanceof Date))) {
          const sanitized = this.sanitizeDocument(value)
          if (sanitized !== undefined) {
            result[key] = sanitized
          }
        } else {
          result[key] = value
        }
      }
      return result as T
    }

    return data
  }

  private normalizeStageRecord(stage: Stage): Stage {
    return {
      ...stage,
      location: normalizeLocation(stage.location),
      estimatedArrival: toDateOrUndefined(stage.estimatedArrival) ?? null,
      estimatedDeparture: toDateOrUndefined(stage.estimatedDeparture) ?? null,
      actualArrival: toDateOrUndefined((stage as any).actualArrival) ?? null,
      actualDeparture: toDateOrUndefined((stage as any).actualDeparture) ?? null,
      completedAt: toDateOrUndefined(stage.completedAt) ?? null,
      createdAt: toDateOrUndefined(stage.createdAt),
      updatedAt: toDateOrUndefined(stage.updatedAt)
    }
  }

  private normalizePlanRecord(plan: Partial<Plan> & { id: string; title?: string }): Plan {
    const name =
      isNonEmptyString(plan.name)
        ? plan.name.trim()
        : isNonEmptyString(plan.title)
          ? plan.title.trim()
          : 'Mon voyage'

    return {
      ...plan,
      name,
      // Preserve stage array shape to avoid undefined downstream
      stages: Array.isArray(plan.stages) ? plan.stages : [],
      isPublic: Boolean(plan.isPublic),
      isDefault: Boolean(plan.isDefault),
      createdAt: toDateOrUndefined((plan as any)?.createdAt),
      updatedAt: toDateOrUndefined((plan as any)?.updatedAt)
    } as Plan
  }

  private async loadOrderedStages(planId: string): Promise<Stage[]> {
    const stagesCollection = this.firestore.collection(this.stagesCollection)
    let stageDocs: FirestoreDoc[] = []

    try {
      const stagesSnapshot = await stagesCollection
        .where('planId', '==', planId)
        .orderBy('order', 'asc')
        .get()
      stageDocs = stagesSnapshot.docs as FirestoreDoc[]
    } catch (error) {
      const err = error as { code?: string; message?: string }
      const missingIndex = err.code === '9' || err.message?.includes('requires an index')

      if (!missingIndex) {
        logger.error('Failed to fetch stages for plan', { planId, error: err?.message ?? err })
        throw error
      }

      logger.warn('Missing Firestore index for plan stages query, using fallback', {
        planId,
        message: err.message
      })

      const fallbackSnapshot = await stagesCollection.where('planId', '==', planId).get()
      stageDocs = fallbackSnapshot.docs as FirestoreDoc[]
      stageDocs.sort((a, b) => {
        const aOrder = (a.data() as Stage).order ?? 0
        const bOrder = (b.data() as Stage).order ?? 0
        if (aOrder !== bOrder) {
          return aOrder - bOrder
        }

        // Secondary ordering by updatedAt/createdAt for stability
        const aTime = toTimestamp((a.data() as Stage).updatedAt ?? (a.data() as Stage).createdAt)
        const bTime = toTimestamp((b.data() as Stage).updatedAt ?? (b.data() as Stage).createdAt)
        return aTime - bTime
      })
    }

    return stageDocs.map((stageDoc) => this.normalizeStageRecord({ id: stageDoc.id, ...stageDoc.data() } as Stage))
  }

  // Plans
  async findPublicPlans(): Promise<Plan[]> {
    logger.debug('Querying public plans')
    const snapshot = await this.firestore
      .collection(this.plansCollection)
      .where('isPublic', '==', true)
      .orderBy('createdAt', 'desc')
      .limit(50) // Limit to 50 most recent public plans
      .get()

    const plans = snapshot.docs.map((doc: FirestoreDoc) =>
      this.normalizePlanRecord({
        id: doc.id,
        ...doc.data(),
        createdAt: toDateOrUndefined((doc.data() as any)?.createdAt),
        updatedAt: toDateOrUndefined((doc.data() as any)?.updatedAt)
      } as Plan)
    )
    
    logger.info('Public plans retrieved', { count: plans.length })
    return plans
  }

  async findPlansByUserId(userId: string): Promise<Plan[]> {
    logger.debug('Querying plans by user (owned + shared)', { userId })
    try {
      const collection = this.firestore.collection(this.plansCollection)
      
      // Query 1: Plans owned by user
      const ownedSnapshot = await collection
        .where('userId', '==', userId)
        .orderBy('createdAt', 'desc')
        .get()

      // Query 2: Plans shared with user (user is in sharedWith array)
      // Note: Firestore array-contains only supports one value at a time
      let sharedSnapshot
      try {
        sharedSnapshot = await collection
          .where('sharedWith', 'array-contains', userId)
          .get()
      } catch (error) {
        // If sharedWith query fails (e.g., no index), log and continue with owned plans only
        logger.warn('Failed to query shared plans, continuing with owned only', { userId, error })
        sharedSnapshot = { docs: [] }
      }

      // Combine results, deduplicating by plan ID
      const planMap = new Map<string, Plan>()
      
      // Add owned plans first
      for (const doc of ownedSnapshot.docs as FirestoreDoc[]) {
        const plan = this.normalizePlanRecord({
          id: doc.id,
          ...doc.data(),
          createdAt: toDateOrUndefined((doc.data() as any)?.createdAt),
          updatedAt: toDateOrUndefined((doc.data() as any)?.updatedAt),
          isOwner: true // Mark as owned
        } as Plan & { isOwner?: boolean })
        planMap.set(doc.id, plan)
      }

      // Add shared plans (only if not already in map)
      for (const doc of sharedSnapshot.docs as FirestoreDoc[]) {
        if (!planMap.has(doc.id)) {
          const plan = this.normalizePlanRecord({
            id: doc.id,
            ...doc.data(),
            createdAt: toDateOrUndefined((doc.data() as any)?.createdAt),
            updatedAt: toDateOrUndefined((doc.data() as any)?.updatedAt),
            isOwner: false // Mark as shared
          } as Plan & { isOwner?: boolean })
          planMap.set(doc.id, plan)
        }
      }

      const plans = Array.from(planMap.values())
      
      // Sort by updatedAt/createdAt descending
      plans.sort((a, b) => {
        const aTime = (a.updatedAt ?? a.createdAt)?.getTime() ?? 0
        const bTime = (b.updatedAt ?? b.createdAt)?.getTime() ?? 0
        return bTime - aTime
      })

      // Load stages for all plans in parallel (properly awaited)
      const plansWithStages = await Promise.all(
        plans.map(async plan => ({
          ...plan,
          stages: await this.loadOrderedStages(plan.id)
        }))
      )

      logger.info('User plans retrieved (owned + shared)', { 
        userId, 
        ownedCount: ownedSnapshot.docs.length,
        sharedCount: sharedSnapshot.docs.length,
        totalCount: plansWithStages.length 
      })
      return plansWithStages
    } catch (error) {
      const err = error as { code?: string; message?: string }
      const missingIndex = err.code === '9' || err.message?.includes('requires an index')

      if (missingIndex) {
        logger.warn('Missing Firestore index for user plans query, falling back to in-memory sort', {
          userId,
          message: err.message
        })

        const fallbackSnapshot = await this.firestore
          .collection(this.plansCollection)
          .where('userId', '==', userId)
          .get()

        const plans = fallbackSnapshot.docs
          .map((doc: FirestoreDoc) =>
            this.normalizePlanRecord({
              id: doc.id,
              ...doc.data(),
              createdAt: toDateOrUndefined((doc.data() as any)?.createdAt),
              updatedAt: toDateOrUndefined((doc.data() as any)?.updatedAt)
            } as Plan)
          )

        plans.sort((a, b) => {
          const aTime = toTimestamp(a.updatedAt ?? a.createdAt)
          const bTime = toTimestamp(b.updatedAt ?? b.createdAt)
          return bTime - aTime
        })

        const plansWithStages = await Promise.all(
          plans.map(async plan => ({
            ...plan,
            stages: await this.loadOrderedStages(plan.id)
          }))
        )

        logger.info('User plans retrieved with fallback sorting', {
          userId,
          count: plansWithStages.length
        })
        return plansWithStages
      }

      logger.error('Failed to fetch plans for user', {
        userId,
        error: err?.message ?? err
      })
      throw error
    }
  }

  async getUserProfile(userId: string): Promise<Record<string, any> | null> {
    logger.debug('Fetching user profile for planner dashboard', { userId })
    const doc = await this.firestore.collection(this.usersCollection).doc(userId).get()
    if (!doc.exists) {
      logger.warn('User profile not found', { userId })
      return null
    }
    return { id: doc.id, ...doc.data() }
  }

  async getPlannerPreferences(userId: string): Promise<PlannerPreferences | null> {
    const profile = await this.getUserProfile(userId)
    return this.extractPlannerPreferences(profile)
  }

  extractPlannerPreferences(profile: Record<string, any> | null): PlannerPreferences | null {
    const raw = profile?.preferences?.planner ?? profile?.preferences?.layout ?? null
    return this.normalizePlannerPreferences(raw)
  }

  private normalizePlannerPreferences(raw: unknown): PlannerPreferences | null {
    if (!raw || typeof raw !== 'object') {
      return null
    }

    const source = raw as Record<string, unknown>

    const favoriteCategories = Array.isArray(source.favoriteCategories)
      ? source.favoriteCategories.filter((value: unknown): value is string => typeof value === 'string')
      : []

    const lastUsedCategories = Array.isArray(source.lastUsedCategories)
      ? source.lastUsedCategories.filter((value: unknown): value is string => typeof value === 'string')
      : undefined

    return {
      layoutPreset: isValidLayoutPreset(source.layoutPreset) ? source.layoutPreset : 'balanced',
      panelOpen: typeof source.panelOpen === 'boolean' ? source.panelOpen : true,
      defaultContext: isValidDashboardContext(source.defaultContext) ? source.defaultContext : 'pre',
      favoriteCategories,
      defaultRadiusKm: typeof source.defaultRadiusKm === 'number' ? source.defaultRadiusKm : 25,
      lastUsedCategories,
      mapPresetId: isValidMapPreset(source.mapPresetId) ? source.mapPresetId : undefined,
      updatedAt: typeof source.updatedAt === 'string' ? source.updatedAt : undefined
    }
  }

  async setPlannerPreferences(userId: string, preferences: PlannerPreferences): Promise<void> {
    const timestamp = new Date().toISOString()
    logger.debug('Updating planner preferences', { userId, preferences })
    const plannerPreferences = this.sanitizeDocument({
      ...preferences,
      mapPresetId: isValidMapPreset(preferences.mapPresetId) ? preferences.mapPresetId : undefined,
      updatedAt: timestamp
    })
    await this.firestore
      .collection(this.usersCollection)
      .doc(userId)
      .set(
        {
          preferences: {
            planner: plannerPreferences,
            updatedAt: timestamp
          }
        },
        { merge: true }
      )
  }

  async findPlanById(planId: string): Promise<Plan | null> {
    logger.debug('Fetching plan by ID', { planId })
    const doc = await this.firestore
      .collection(this.plansCollection)
      .doc(planId)
      .get()

    if (!doc.exists) {
      logger.warn('Plan not found', { planId })
      return null
    }

    const plan = this.normalizePlanRecord({
      id: doc.id,
      ...doc.data(),
      createdAt: toDateOrUndefined((doc.data() as any)?.createdAt),
      updatedAt: toDateOrUndefined((doc.data() as any)?.updatedAt)
    } as Plan)
    plan.stages = await this.loadOrderedStages(planId)

    logger.info('Plan fetched', { planId, stagesCount: plan.stages.length })
    return plan
  }

  async findStageById(stageId: string): Promise<Stage | null> {
    logger.debug('Fetching stage by ID', { stageId })
    const doc = await this.firestore
      .collection(this.stagesCollection)
      .doc(stageId)
      .get()

    if (!doc.exists) {
      logger.warn('Stage not found', { stageId })
      return null
    }

    const rawStage = { id: doc.id, ...doc.data() } as Stage
    logger.info('Stage fetched', { stageId })
    return this.normalizeStageRecord(rawStage)
  }

  async createPlan(dto: CreatePlanDTO, userId: string): Promise<Plan> {
    logger.info('Creating plan', { userId, planName: dto.name })
    const now = new Date()
    
    const planData: Omit<Plan, 'id'> & { title?: string } = {
      name: dto.name,
      title: dto.name, // Keep legacy compatibility (older clients still read title)
      description: dto.description,
      userId,
      stages: [],
      isPublic: dto.isPublic || false,
      isDefault: dto.isDefault ?? false,
      settings: dto.settings as RouteSettings | undefined,
      createdAt: now,
      updatedAt: now
    }

    const sanitizedPlanData = this.sanitizeDocument(planData)
    const { title: _title, ...planPayload } = sanitizedPlanData

    const docRef = await this.firestore
      .collection(this.plansCollection)
      .add(sanitizedPlanData)

    logger.info('Plan created in Firestore', { planId: docRef.id, userId })
    return { id: docRef.id, ...planPayload } as Plan
  }

  async createPlanWithStages(
    userId: string,
    data: {
      name: string
      description?: string
      isPublic?: boolean
      tags?: string[]
      settings?: Partial<RouteSettings>
      routeData?: RouteData
      aiMetadata?: AIGenerationMetadata
      isDefault?: boolean
    },
    stages: Array<Omit<Stage, 'id' | 'planId'> & { planId?: string }>
  ): Promise<Plan> {
    logger.info('Creating plan with stages', { userId, planName: data.name, stagesCount: stages.length })
    const now = new Date()
    const planRef = this.firestore.collection(this.plansCollection).doc()

    const planData: Omit<Plan, 'id'> & { title?: string } = {
      name: data.name,
      title: data.name, // Keep legacy compatibility (older clients still read title)
      description: data.description,
      userId,
      stages: [],
      isPublic: data.isPublic ?? false,
      isDefault: data.isDefault ?? false,
      settings: data.settings as RouteSettings | undefined,
      routeData: data.routeData,
      tags: data.tags,
      aiMetadata: data.aiMetadata,
      createdAt: now,
      updatedAt: now
    }

    const sanitizedPlanData = this.sanitizeDocument(planData)
    const { title: _title, ...planPayload } = sanitizedPlanData

    const batch = createBatchBuilder(this.firestore)
    batch.setRef(planRef, sanitizedPlanData)

    const createdStages: Stage[] = []
    let index = 0
    for (const stage of stages) {
      const stageId = batch.addSet(this.stagesCollection, null, {})
      const stageData: Stage = {
        ...stage,
        id: stageId,
        planId: planRef.id,
        order: stage.order ?? index,
        createdAt: now,
        updatedAt: now
      } as Stage

      if (stageData.location) {
        stageData.location = normalizeLocation(stageData.location)
      }

      const sanitizedStageData = this.sanitizeDocument(stageData) as Stage
      batch.addUpdate(this.stagesCollection, stageId, sanitizedStageData as unknown as Record<string, unknown>)
      createdStages.push(sanitizedStageData)
      index += 1
    }

    await batch.commit()
    logger.info('Plan with stages created', { planId: planRef.id, userId, stagesCount: createdStages.length })

    return {
      id: planRef.id,
      ...planPayload,
      stages: createdStages
    }
  }

  async updatePlan(planId: string, updates: UpdatePlanDTO): Promise<Plan | null> {
    logger.info('Updating plan', { planId, updateFields: Object.keys(updates).length })
    const docRef = this.firestore
      .collection(this.plansCollection)
      .doc(planId)

    const payload = this.sanitizeDocument({
      ...updates,
      ...(isNonEmptyString(updates.name) ? { title: updates.name } : {}),
      updatedAt: new Date()
    })

    await docRef.update(payload)

    logger.info('Plan updated in Firestore', { planId })
    return this.findPlanById(planId)
  }

  async replaceStagesForPlan(planId: string, stagesDto: CreateStageDTO[]): Promise<Stage[]> {
    logger.info('Replacing all stages for plan', { planId, newStageCount: stagesDto.length })
    const batch = createBatchBuilder(this.firestore)
    const now = new Date()

    // 1. Delete all existing stages for the plan
    const stagesCollection = this.firestore.collection(this.stagesCollection)
    const existingStagesSnapshot = await stagesCollection.where('planId', '==', planId).get()

    logger.debug('Found existing stages to delete', { planId, count: existingStagesSnapshot.size })
    for (const doc of existingStagesSnapshot.docs) {
      batch.deleteRef(doc.ref)
    }

    // 2. Create new stages
    const createdStages: Stage[] = []
    for (const dto of stagesDto) {
      const location = normalizeLocation(dto.location)

      const newStageData: Omit<Stage, 'id'> = {
        ...dto,
        planId,
        location,
        createdAt: now,
        updatedAt: now
      }

      const sanitizedStageData = this.sanitizeDocument(newStageData)
      const stageId = batch.addSet(this.stagesCollection, null, sanitizedStageData)
      createdStages.push({ id: stageId, ...sanitizedStageData } as Stage)
    }

    // 3. Update the plan's updatedAt timestamp
    batch.touchUpdatedAt(this.plansCollection, planId, now)

    await batch.commit()
    logger.info('Successfully replaced stages for plan', { planId, newStageCount: createdStages.length })

    return createdStages
  }

  async findDefaultPlanByUserId(userId: string): Promise<Plan | null> {
    logger.debug('Fetching default plan for user', { userId })
    const snapshot = await this.firestore
      .collection(this.plansCollection)
      .where('userId', '==', userId)
      .where('isDefault', '==', true)
      .limit(1)
      .get()

    if (snapshot.empty) {
      return null
    }

    const doc = snapshot.docs[0]
    return this.findPlanById(doc.id)
  }

  async findMostRecentPlanByUserId(userId: string): Promise<Plan | null> {
    logger.debug('Fetching most recent plan for user', { userId })
    try {
      const snapshot = await this.firestore
        .collection(this.plansCollection)
        .where('userId', '==', userId)
        .orderBy('updatedAt', 'desc')
        .limit(1)
        .get()

      if (snapshot.empty) {
        return null
      }

      const doc = snapshot.docs[0]
      return this.findPlanById(doc.id)
    } catch (error) {
      const err = error as { code?: string; message?: string }
      const missingIndex = err.code === '9' || err.message?.includes('requires an index')

      if (!missingIndex) {
        logger.error('Failed to fetch most recent plan', { userId, error: err?.message ?? err })
        throw error
      }

      logger.warn('Missing Firestore index for most recent plan query, using fallback', {
        userId,
        message: err.message
      })

      const fallbackSnapshot = await this.firestore
        .collection(this.plansCollection)
        .where('userId', '==', userId)
        .get()

      if (fallbackSnapshot.empty) {
        return null
      }

      const normalize = (value: unknown): number => {
        if (!value) return 0
        if (value instanceof Date) return value.getTime()
        if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
          return (value as { toDate: () => Date }).toDate().getTime()
        }
        const parsed = Date.parse(String(value))
        return Number.isNaN(parsed) ? 0 : parsed
      }

      const doc = fallbackSnapshot.docs
        .map((snapshotDoc: FirestoreDoc) => ({
          doc: snapshotDoc,
          data: snapshotDoc.data()
        }))
        .sort((a, b) => {
          const aTime = normalize((a.data as Plan).updatedAt ?? (a.data as Plan).createdAt)
          const bTime = normalize((b.data as Plan).updatedAt ?? (b.data as Plan).createdAt)
          return bTime - aTime
        })
        .map(item => item.doc)[0]

      return doc ? this.findPlanById(doc.id) : null
    }
  }

  async setDefaultPlan(userId: string, planId: string): Promise<void> {
    logger.info('Setting default plan', { userId, planId })
    const collection = this.firestore.collection(this.plansCollection)
    const batch = createBatchBuilder(this.firestore)
    const now = new Date()

    let defaultDocs: FirestoreDoc[] = []
    try {
      const defaultsSnapshot = await collection
        .where('userId', '==', userId)
        .where('isDefault', '==', true)
        .get()
      defaultDocs = defaultsSnapshot.docs as FirestoreDoc[]
    } catch (error) {
      const err = error as { code?: string; message?: string }
      const missingIndex = err.code === '9' || err.message?.includes('requires an index')

      if (!missingIndex) {
        logger.error('Failed to resolve default plans', { userId, error: err?.message ?? err })
        throw error
      }

      logger.warn('Missing Firestore index for default plan query, using simple scan', {
        userId,
        message: err.message
      })

      const fallbackSnapshot = await collection.where('userId', '==', userId).get()
      defaultDocs = fallbackSnapshot.docs.filter(doc => {
        const data = doc.data() as { isDefault?: boolean }
        return data.isDefault === true
      }) as FirestoreDoc[]
    }

    for (const doc of defaultDocs) {
      if (doc.id === planId) continue
      batch.updateRef(doc.ref, { isDefault: false, updatedAt: now })
    }

    batch.addUpdate(this.plansCollection, planId, { isDefault: true, updatedAt: now })

    await batch.commit()
    logger.info('Default plan updated', { userId, planId })
  }

  /**
   * Delete a plan and ALL related data (cascade delete).
   * 
   * Uses SmartBatch to handle Firestore's 500 operation limit.
   * Supports both hard delete and soft delete modes.
   * 
   * This includes:
   * - Plan document
   * - All stages
   * - Share links
   * - Budgets and expenses
   * - CRDT snapshots
   * 
   * Note: Redis cleanup (ydoc, presence, cache) is handled by PlannerService.
   * 
   * @param planId - The plan ID to delete
   * @param options - Delete options
   * @param options.softDelete - If true, marks as deleted instead of removing (default: false)
   */
  async deletePlan(
    planId: string, 
    options: { softDelete?: boolean } = {}
  ): Promise<{ deletedCount: Record<string, number> }> {
    const { softDelete = false } = options
    const deleteMode = softDelete ? 'soft' : 'hard'
    
    logger.info('Deleting plan with full cascade', { planId, mode: deleteMode })
    
    const batch = new SmartBatch(this.firestore)
    const deletedCount = { 
      plan: 0,
      stages: 0, 
      shareLinks: 0, 
      budgets: 0, 
      expenses: 0, 
      planExpenses: 0, 
      snapshots: 0 
    }

    // 1. Delete/soft-delete plan document
    const planRef = this.firestore
      .collection(this.plansCollection)
      .doc(planId)
    
    if (softDelete) {
      batch.softDelete(planRef)
    } else {
      batch.delete(planRef)
    }
    deletedCount.plan = 1

    // 2. Delete all stages
    const stagesSnapshot = await this.firestore
      .collection(this.stagesCollection)
      .where('planId', '==', planId)
      .get()
    for (const doc of stagesSnapshot.docs) {
      if (softDelete) {
        batch.softDelete(doc.ref)
      } else {
        batch.delete(doc.ref)
      }
      deletedCount.stages++
    }

    // 3. Delete share links referencing this plan
    const shareLinksSnapshot = await this.firestore
      .collection('shareLinks')
      .where('planId', '==', planId)
      .get()
    for (const doc of shareLinksSnapshot.docs) {
      // Share links are always hard deleted (no need to recover)
      batch.delete(doc.ref)
      deletedCount.shareLinks++
    }

    // 4. Delete budgets and their expenses
    const budgetsSnapshot = await this.firestore
      .collection('budgets')
      .where('planId', '==', planId)
      .get()
    for (const budgetDoc of budgetsSnapshot.docs) {
      // Delete expenses for this budget
      const expensesSnapshot = await this.firestore
        .collection('expenses')
        .where('budgetId', '==', budgetDoc.id)
        .get()
      for (const expenseDoc of expensesSnapshot.docs) {
        if (softDelete) {
          batch.softDelete(expenseDoc.ref)
        } else {
          batch.delete(expenseDoc.ref)
        }
        deletedCount.expenses++
      }
      if (softDelete) {
        batch.softDelete(budgetDoc.ref)
      } else {
        batch.delete(budgetDoc.ref)
      }
      deletedCount.budgets++
    }

    // 5. Delete plan_expenses
    const planExpensesSnapshot = await this.firestore
      .collection('plan_expenses')
      .where('planId', '==', planId)
      .get()
    for (const doc of planExpensesSnapshot.docs) {
      if (softDelete) {
        batch.softDelete(doc.ref)
      } else {
        batch.delete(doc.ref)
      }
      deletedCount.planExpenses++
    }

    // 6. Delete CRDT snapshots (subcollection) - always hard delete
    const snapshotsSnapshot = await this.firestore
      .collection(this.plansCollection)
      .doc(planId)
      .collection('snapshots')
      .get()
    for (const doc of snapshotsSnapshot.docs) {
      batch.delete(doc.ref)
      deletedCount.snapshots++
    }

    // Commit all deletions (SmartBatch handles 500 limit automatically)
    const result = await batch.commit()
    
    logger.info('Plan deleted with full cascade', { 
      planId, 
      mode: deleteMode,
      deleted: deletedCount,
      batchStats: {
        totalOperations: result.totalOperations,
        batchesCommitted: result.batchesCommitted
      }
    })

    return { deletedCount }
  }

  /**
   * Restore a soft-deleted plan and all its related data.
   */
  async restorePlan(planId: string): Promise<void> {
    logger.info('Restoring soft-deleted plan', { planId })
    
    const batch = new SmartBatch(this.firestore)
    const now = new Date()
    
    // Restore plan
    const planRef = this.firestore.collection(this.plansCollection).doc(planId)
    batch.update(planRef, { [SOFT_DELETE_FIELD]: null, updatedAt: now })
    
    // Restore stages
    const stagesSnapshot = await this.firestore
      .collection(this.stagesCollection)
      .where('planId', '==', planId)
      .get()
    for (const doc of stagesSnapshot.docs) {
      batch.update(doc.ref, { [SOFT_DELETE_FIELD]: null, updatedAt: now })
    }
    
    // Restore budgets and expenses
    const budgetsSnapshot = await this.firestore
      .collection('budgets')
      .where('planId', '==', planId)
      .get()
    for (const budgetDoc of budgetsSnapshot.docs) {
      const expensesSnapshot = await this.firestore
        .collection('expenses')
        .where('budgetId', '==', budgetDoc.id)
        .get()
      for (const expenseDoc of expensesSnapshot.docs) {
        batch.update(expenseDoc.ref, { [SOFT_DELETE_FIELD]: null, updatedAt: now })
      }
      batch.update(budgetDoc.ref, { [SOFT_DELETE_FIELD]: null, updatedAt: now })
    }
    
    // Restore plan_expenses
    const planExpensesSnapshot = await this.firestore
      .collection('plan_expenses')
      .where('planId', '==', planId)
      .get()
    for (const doc of planExpensesSnapshot.docs) {
      batch.update(doc.ref, { [SOFT_DELETE_FIELD]: null, updatedAt: now })
    }
    
    await batch.commit()
    logger.info('Plan restored successfully', { planId })
  }

  // Stages
  async createStage(planId: string, dto: CreateStageDTO): Promise<Stage> {
    logger.info('Creating stage', { planId })
    // Validate order (security check)
    if (typeof dto.order !== 'number' || dto.order < 0 || dto.order > 10000) {
      logger.warn('Invalid order value for stage', { planId, order: dto.order })
      throw new Error(`Invalid order value: ${dto.order}. Must be between 0 and 10000.`)
    }

    const batch = createBatchBuilder(this.firestore)
    const now = new Date()
    const location = normalizeLocation(dto.location)

    const stageData = {
      ...dto,
      location,
      planId,
      pois: [],
      createdAt: now,
      updatedAt: now
    }

    const sanitizedStageData = this.sanitizeDocument(stageData)
    const stageId = batch.addSet(this.stagesCollection, null, sanitizedStageData)
    batch.touchUpdatedAt(this.plansCollection, planId, now)

    await batch.commit()
    logger.info('Stage created in Firestore', { planId, stageId })

    return { id: stageId, ...sanitizedStageData } as Stage
  }

  async updateStage(stageId: string, updates: UpdateStageDTO): Promise<Stage | null> {
    logger.debug('Updating stage in Firestore', { stageId })
    const docRef = this.firestore
      .collection(this.stagesCollection)
      .doc(stageId)

    const doc = await docRef.get()
    if (!doc.exists) {
      logger.warn('Stage not found for update', { stageId })
      return null
    }

    const batch = createBatchBuilder(this.firestore)
    const now = new Date()

    const payload: Record<string, unknown> = {
      ...updates,
      updatedAt: now
    }

    if (updates.location) {
      payload.location = normalizeLocation(updates.location)
    }

    const sanitizedPayload = this.sanitizeDocument(payload)
    const stageData = doc.data() as Stage

    batch
      .addUpdate(this.stagesCollection, stageId, sanitizedPayload)
      .touchUpdatedAt(this.plansCollection, stageData.planId, now)

    await batch.commit()
    logger.info('Stage updated in Firestore', { stageId, planId: stageData.planId })

    const updated = await docRef.get()
    return { id: updated.id, ...updated.data() } as Stage
  }

  async deleteStage(stageId: string): Promise<void> {
    logger.debug('Deleting stage in Firestore', { stageId })
    const doc = await this.firestore
      .collection(this.stagesCollection)
      .doc(stageId)
      .get()

    if (!doc.exists) {
      logger.warn('Stage not found for deletion', { stageId })
      return
    }

    const stageData = doc.data() as Stage
    const batch = createBatchBuilder(this.firestore)
    const now = new Date()

    batch
      .deleteRef(doc.ref)
      .touchUpdatedAt(this.plansCollection, stageData.planId, now)

    await batch.commit()
    logger.info('Stage deleted in Firestore', { stageId, planId: stageData.planId })
  }

  async updateStagesBatch(stages: Stage[]): Promise<void> {
    if (stages.length === 0) return
    logger.debug('Batch updating stages', { count: stages.length, planId: stages[0].planId })

    const batch = createBatchBuilder(this.firestore)
    const now = new Date()

    for (const stage of stages) {
      // Sanitize to remove undefined values (Firestore batch.update() doesn't accept them)
      const stageData = this.sanitizeDocument({ ...stage, updatedAt: now })
      batch.addUpdate(this.stagesCollection, stage.id, stageData)
    }

    batch.touchUpdatedAt(this.plansCollection, stages[0].planId, now)

    await batch.commit()
    logger.info('Batch stage update committed', { planId: stages[0].planId, count: stages.length })
  }

  /**
   * Upsert stages coming from collaboration (CRDT) and remove deleted ones.
   * Ensures Firestore remains the canonical source of truth after merges.
   */
  async upsertStagesFromCollab(planId: string, stages: Stage[]): Promise<void> {
    logger.debug('Upserting collab stages', { planId, count: stages.length })
    const now = new Date()
    const stageIds = new Set(stages.map(stage => stage.id))

    const stagesCollection = this.firestore.collection(this.stagesCollection)
    const existingSnapshot = await stagesCollection.where('planId', '==', planId).get()
    const existingById = new Map(
      existingSnapshot.docs.map(doc => {
        const rawStage = doc.data() as Stage
        const { id: _ignoredId, ...rest } = rawStage
        const normalizedStage: Stage = { ...rest, id: doc.id }
        return [doc.id, normalizedStage]
      })
    )

    const batch = createBatchBuilder(this.firestore)

    for (const stage of stages) {
      if (!stage?.id) continue

      const existing = existingById.get(stage.id)
      const createdAt = toDateOrUndefined(stage.createdAt ?? existing?.createdAt) ?? now
      const allowedTypes: Stage['type'][] = ['start', 'waypoint', 'end', 'overnight', 'poi']
      const stageType =
        allowedTypes.includes(stage.type as Stage['type'])
          ? (stage.type as Stage['type'])
          : existing?.type ?? 'waypoint'
      const normalizedLoc = normalizeLocation(stage.location)
      const sanitizedStage = this.sanitizeDocument({
        ...stage,
        planId,
        name: isNonEmptyString(stage.name) ? stage.name.trim() : existing?.name ?? 'Étape',
        order: typeof stage.order === 'number' ? stage.order : existing?.order ?? 0,
        type: stageType,
        location: normalizedLoc,
        createdAt,
        updatedAt: now
      })

      batch.addSet(this.stagesCollection, stage.id, sanitizedStage, { merge: true })
    }

    for (const doc of existingSnapshot.docs) {
      if (!stageIds.has(doc.id)) {
        batch.deleteRef(doc.ref)
      }
    }

    batch.touchUpdatedAt(this.plansCollection, planId, now)

    await batch.commit()
    const removedCount = Math.max(0, existingSnapshot.size - stageIds.size)
    logger.info('Collab stages upserted', { planId, inserted: stages.length, removed: removedCount })
  }

  /**
   * Find stages by multiple IDs (for route optimization)
   */
  async findStagesByIds(stageIds: string[]): Promise<Stage[]> {
    logger.debug('Fetching stages by IDs', { count: stageIds.length })
    
    if (stageIds.length === 0) {
      return []
    }

    // Firestore 'in' query limited to 10 items, so batch if needed
    const batches: string[][] = []
    for (let i = 0; i < stageIds.length; i += 10) {
      batches.push(stageIds.slice(i, i + 10))
    }

    const allStages: Stage[] = []
    
    for (const batch of batches) {
      const snapshot = await this.firestore
        .collection(this.stagesCollection)
        .where('__name__', 'in', batch)
        .get()

      for (const doc of snapshot.docs) {
        const stage = { id: doc.id, ...doc.data() } as Stage
        allStages.push(this.normalizeStageRecord(stage))
      }
    }

    // Sort by original stageIds order
    const orderMap = new Map(stageIds.map((id, index) => [id, index]))
    allStages.sort((a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0))

    logger.info('Stages fetched by IDs', { requested: stageIds.length, found: allStages.length })
    return allStages
  }

  /**
   * Find all stages for a plan (for route optimization)
   */
  async findByPlanId(planId: string): Promise<Stage[]> {
    logger.debug('Fetching all stages for plan', { planId })
    
    const snapshot = await this.firestore
      .collection(this.stagesCollection)
      .where('planId', '==', planId)
      .orderBy('order', 'asc')
      .get()

    const stages: Stage[] = []
    for (const doc of snapshot.docs) {
      const stage = { id: doc.id, ...doc.data() } as Stage
      stages.push(this.normalizeStageRecord(stage))
    }

    logger.info('Stages fetched for plan', { planId, count: stages.length })
    return stages
  }

  /**
   * Alias for findPlanById (for consistency with route optimization controller)
   */
  async findById(planId: string): Promise<Plan | null> {
    return this.findPlanById(planId)
  }
}
